import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { CheckCircleIcon, ClipboardListIcon, XCircleIcon } from 'lucide-react';
import { Encounter } from '../../types';
type Props = {
  onBack: () => void;
};
export function PharmacyDispensing({
  onBack
}: Props) {
  const {
    encounters,
    patients,
    currentUser,
    updateEncounter,
    dispenseMedicine
  } = useData();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, 'Dispensed' | 'Not Dispensed'>>({});
  const [message, setMessage] = useState('');
  const selected = selectedId ? encounters.find(encounter => encounter.id === selectedId) || null : null;
  const pending = encounters.filter(encounter => encounter.stage === 'PHARMACY');
  const patient = selected ? patients.find(item => item.id === selected.patientId) : undefined;
  const selectEncounter = (encounter: Encounter) => {
    setSelectedId(encounter.id);
    setStatuses(encounter.prescriptions.reduce<Record<string, 'Dispensed' | 'Not Dispensed'>>((nextStatuses, prescription) => {
      if (prescription.status !== 'Pending') {
        nextStatuses[prescription.id] = prescription.status;
      }
      return nextStatuses;
    }, {}));
    setMessage('');
  };
  const markDispensed = async (prescriptionId: string, drugName: string) => {
    if (!selected) return;
    const result = await dispenseMedicine({
      drugName,
      encounterId: selected.id,
      patientId: selected.patientId,
      prescriptionId
    });
    if (!result.ok) {
      setMessage(result.error || 'Unable to dispense this medicine.');
      return;
    }
    setStatuses(previous => ({
      ...previous,
      [prescriptionId]: 'Dispensed'
    }));
    setMessage('One unit was deducted using FEFO from an eligible unexpired batch and recorded in stock history.');
  };
  const markNotAvailable = (prescriptionId: string) => setStatuses(previous => ({
    ...previous,
    [prescriptionId]: 'Not Dispensed'
  }));
  const complete = () => {
    if (!selected || !currentUser) return;
    if (!selected.prescriptions.every(prescription => statuses[prescription.id])) {
      setMessage('Mark each prescription as Dispensed or Not Available before completing.');
      return;
    }
    updateEncounter(selected.id, {
      prescriptions: selected.prescriptions.map(prescription => {
        const status = statuses[prescription.id] || prescription.status;
        return {
          ...prescription,
          status,
          ...(status === 'Dispensed' ? {
            dispensedAt: new Date().toISOString(),
            dispensedBy: currentUser.id,
            pharmacistName: currentUser.fullName
          } : {})
        };
      }),
      stage: 'CLINICIAN'
    });
    setSelectedId(null);
    setStatuses({});
    setMessage('Dispensing recorded and patient file returned to the clinician.');
  };
  if (selected && patient) return <div className="space-y-5" data-id="element-4774">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-4775">
          <div data-id="element-4776">
            <p className="text-sm font-semibold text-pink-600" data-id="element-4777">Dispensing</p>
            <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-4778">
              {patient.firstName} {patient.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-4779">
              {patient.bmcFileNumber} · One unit is deducted per item marked
              dispensed.
            </p>
          </div>
          <div className="flex gap-2" data-id="element-4780">
            <Button variant="outline" onClick={() => {
          setSelectedId(null);
          setStatuses({});
          setMessage('');
        }} data-id="element-4781">
              Back to queue
            </Button>
            <Button onClick={complete} className="bg-teal-600 hover:bg-teal-700" data-id="element-4782">
              <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-4783" /> Complete dispensing
            </Button>
          </div>
        </div>
        {message && <p role="status" className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800" data-id="element-4784">
            {message}
          </p>}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4785">
          <div className="border-b border-slate-100 px-5 py-4" data-id="element-4786">
            <h3 className="font-bold text-slate-900" data-id="element-4787">Prescribed medicines</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-4788">
              Dispense checks inventory by medicine or generic name. If no
              matching stock is found, use Not Available.
            </p>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-4789">
            {selected.prescriptions.map(prescription => <article key={prescription.id} className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-4790">
                <div data-id="element-4791">
                  <h4 className="font-semibold text-slate-900" data-id="element-4792">
                    {prescription.drugName}
                  </h4>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-4793">
                    {prescription.dose} · {prescription.route} ·{' '}
                    {prescription.frequency} · {prescription.days} days
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-4794">
                  <span className={`mr-1 rounded-full px-2 py-1 text-xs font-semibold ${statuses[prescription.id] === 'Dispensed' ? 'bg-emerald-50 text-emerald-700' : statuses[prescription.id] === 'Not Dispensed' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-4795">
                    {statuses[prescription.id] || 'Awaiting action'}
                  </span>
                  <Button size="sm" disabled={Boolean(statuses[prescription.id])} onClick={() => markDispensed(prescription.id, prescription.drugName)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4796">
                    <CheckCircleIcon className="mr-1.5 h-4 w-4" data-id="element-4797" /> Dispense
                  </Button>
                  <Button size="sm" variant="outline" disabled={Boolean(statuses[prescription.id])} onClick={() => markNotAvailable(prescription.id)} className="border-red-200 text-red-700 hover:bg-red-50" data-id="element-4798">
                    <XCircleIcon className="mr-1.5 h-4 w-4" data-id="element-4799" /> Not available
                  </Button>
                </div>
              </article>)}
          </div>
        </section>
      </div>;
  return <div className="space-y-5" data-id="element-4800">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-4801">
        <div data-id="element-4802">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4803">Pharmacy desk</p>
          <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-4804">
            Dispensing queue
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4805">
            Patient prescriptions waiting for pharmacy action.
          </p>
        </div>
        <Button variant="outline" onClick={onBack} data-id="element-4806">
          Back to overview
        </Button>
      </div>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4807">
        <div className="divide-y divide-slate-100" data-id="element-4808">
          {pending.map(encounter => {
          const queuePatient = patients.find(item => item.id === encounter.patientId);
          if (!queuePatient) return null;
          return <article key={encounter.id} className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-4809">
                <div className="flex items-center gap-3" data-id="element-4810">
                  <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700" data-id="element-4811">
                    <ClipboardListIcon className="h-5 w-5" data-id="element-4812" />
                  </div>
                  <div data-id="element-4813">
                    <p className="font-semibold text-slate-900" data-id="element-4814">
                      {queuePatient.firstName} {queuePatient.lastName}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-500" data-id="element-4815">
                      {queuePatient.bmcFileNumber} ·{' '}
                      {encounter.prescriptions.length} prescribed items
                    </p>
                  </div>
                </div>
                <Button size="sm" onClick={() => selectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4816">
                  Review prescription
                </Button>
              </article>;
        })}
          {!pending.length && <div className="px-5 py-14 text-center" data-id="element-4817">
              <ClipboardListIcon className="mx-auto h-8 w-8 text-slate-300" data-id="element-4818" />
              <p className="mt-3 text-sm text-slate-500" data-id="element-4819">
                No prescriptions are waiting for pharmacy.
              </p>
            </div>}
        </div>
      </section>
    </div>;
}